import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { projectsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  FolderKanban,
  Leaf,
  TrendingDown,
  Calculator,
  Plus,
  ArrowRight,
  Building2,
  Activity,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const Dashboard = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProjects: 0,
    totalCarbon: 0,
    avgCarbon: 0,
    totalOptions: 0,
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      const projectsData = response.data;
      setProjects(projectsData);

      // Calculate stats
      const totalProjects = projectsData.length;
      let totalCarbon = 0;
      let totalOptions = 0;

      projectsData.forEach((project) => {
        project.design_options?.forEach((opt) => {
          totalCarbon += opt.total_carbon || 0;
          totalOptions++;
        });
      });

      setStats({
        totalProjects,
        totalCarbon: Math.round(totalCarbon),
        avgCarbon: totalOptions > 0 ? Math.round(totalCarbon / totalOptions) : 0,
        totalOptions,
      });
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  // Chart data
  const carbonByCategory = {};
  projects.forEach((project) => {
    project.design_options?.forEach((opt) => {
      Object.entries(opt.carbon_by_category || {}).forEach(([cat, value]) => {
        carbonByCategory[cat] = (carbonByCategory[cat] || 0) + value;
      });
    });
  });

  const categoryChartData = Object.entries(carbonByCategory)
    .map(([name, value]) => ({ name, value: Math.round(value) }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 6);

  const COLORS = ['#0F2818', '#800020', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899'];

  const recentProjects = [...projects]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 5);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-4 border-[#0F2818] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="page-header -mx-8 -mt-8 mb-8 px-8 py-6 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Welcome back, {user?.full_name?.split(' ')[0]}
            </h1>
            <p className="text-slate-600 mt-1">
              Here's an overview of your sustainability metrics
            </p>
          </div>
          <Link to="/projects">
            <Button className="bg-[#0F2818] hover:bg-[#06140C]" data-testid="new-project-button">
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid - Bento Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="bento-card border-slate-200" data-testid="stat-total-projects">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#E8EEEA] flex items-center justify-center">
                <FolderKanban className="w-6 h-6 text-[#0F2818]" />
              </div>
              <div>
                <p className="text-sm text-slate-500 font-medium">Total Projects</p>
                <p className="text-2xl font-bold text-slate-900">{stats.totalProjects}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bento-card border-slate-200" data-testid="stat-total-carbon">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#FBEFD9] flex items-center justify-center">
                <Leaf className="w-6 h-6 text-[#800020]" />
              </div>
              <div>
                <p className="text-sm text-slate-500 font-medium">Total Carbon</p>
                <p className="text-2xl font-bold text-slate-900">
                  {(stats.totalCarbon / 1000).toFixed(1)}
                  <span className="text-sm font-normal text-slate-500 ml-1">tCO2e</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bento-card border-slate-200" data-testid="stat-design-options">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#DBEAFE] flex items-center justify-center">
                <Calculator className="w-6 h-6 text-[#3B82F6]" />
              </div>
              <div>
                <p className="text-sm text-slate-500 font-medium">Design Options</p>
                <p className="text-2xl font-bold text-slate-900">{stats.totalOptions}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bento-card border-slate-200" data-testid="stat-avg-carbon">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#E8EEEA] flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-[#10B981]" />
              </div>
              <div>
                <p className="text-sm text-slate-500 font-medium">Avg Carbon/Option</p>
                <p className="text-2xl font-bold text-slate-900">
                  {(stats.avgCarbon / 1000).toFixed(2)}
                  <span className="text-sm font-normal text-slate-500 ml-1">tCO2e</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Carbon by Category */}
        <Card className="border-slate-200" data-testid="chart-carbon-by-category">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Carbon by Material Category
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {categoryChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={categoryChartData} layout="vertical">
                  <XAxis type="number" tickFormatter={(v) => `${(v / 1000).toFixed(1)}t`} />
                  <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value) => [`${(value / 1000).toFixed(2)} tCO2e`, 'Carbon']}
                    contentStyle={{ borderRadius: 6, border: '1px solid #E2E8F0' }}
                  />
                  <Bar dataKey="value" fill="#0F2818" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                <Activity className="w-12 h-12 mb-4 text-slate-300" />
                <p>No carbon data yet</p>
                <p className="text-sm">Create a project to see analytics</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Carbon Distribution Pie */}
        <Card className="border-slate-200" data-testid="chart-carbon-distribution">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Carbon Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {categoryChartData.length > 0 ? (
              <div className="flex items-center">
                <ResponsiveContainer width="50%" height={280}>
                  <PieChart>
                    <Pie
                      data={categoryChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      dataKey="value"
                      paddingAngle={2}
                    >
                      {categoryChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [`${(value / 1000).toFixed(2)} tCO2e`, 'Carbon']}
                      contentStyle={{ borderRadius: 6, border: '1px solid #E2E8F0' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="w-1/2 pl-4">
                  <ul className="space-y-2">
                    {categoryChartData.map((item, index) => (
                      <li key={item.name} className="flex items-center gap-2 text-sm">
                        <span
                          className="w-3 h-3 rounded-sm flex-shrink-0"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-slate-600 truncate">{item.name}</span>
                        <span className="ml-auto font-mono text-slate-900">
                          {(item.value / 1000).toFixed(1)}t
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                <Leaf className="w-12 h-12 mb-4 text-slate-300" />
                <p>No distribution data</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Projects */}
      <Card className="border-slate-200" data-testid="recent-projects">
        <CardHeader className="border-b border-slate-100 pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Recent Projects
            </CardTitle>
            <Link to="/projects">
              <Button variant="ghost" size="sm" className="text-[#0F2818]">
                View all
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {recentProjects.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {recentProjects.map((project) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`project-${project.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#E8EEEA] flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-[#0F2818]" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{project.name}</p>
                      <p className="text-sm text-slate-500">
                        {project.building_type || 'Building'} • {project.design_options?.length || 0} options
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm font-medium text-slate-900">
                      {project.design_options?.reduce((acc, opt) => acc + (opt.total_carbon || 0), 0) > 0
                        ? `${(project.design_options.reduce((acc, opt) => acc + opt.total_carbon, 0) / 1000).toFixed(2)} tCO2e`
                        : '—'}
                    </p>
                    <p className="text-xs text-slate-500">
                      {new Date(project.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <FolderKanban className="w-12 h-12 mb-4 text-slate-300" />
              <p className="mb-4">No projects yet</p>
              <Link to="/projects">
                <Button className="bg-[#0F2818] hover:bg-[#06140C]">
                  <Plus className="w-4 h-4 mr-2" />
                  Create your first project
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
